import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  isPGRST200Error,
  normalizePaidFields,
  fetchCategoriesMap,
} from '@/lib/supabaseResilient';
import {
  AccrualTransaction,
  AccrualPeriod,
  AccrualFilters,
  AccrualGrouped,
  isTransactionInPeriod,
} from '@/types/accrual';
import {
  transformToAccrualTransaction,
  calculateAccrualSummary,
  groupByCategory,
  groupByStatus,
} from '@/lib/accrual-transformers';

export interface UseAccrualDataOptions {
  period: AccrualPeriod;
  filters?: AccrualFilters;
  enabled?: boolean;
}

export interface UseAccrualDataReturn {
  data: AccrualGrouped | null;
  transactions: AccrualTransaction[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useAccrualData(
  options: UseAccrualDataOptions
): UseAccrualDataReturn {
  const { period, filters, enabled = true } = options;
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: ['accrual-data', period.month, period.year, filters],
    queryFn: async () => {
      console.log('Fetching accrual data for period:', period.label);

      // Get authenticated user
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        console.error('Authentication error:', userError);
        throw new Error('Usuário não autenticado');
      }

      // Estratégia resiliente monolab: embedded category pode dar PGRST200 (sem FK)
      const fetchAccrualChunk = async (
        withEmbedded: boolean,
        withCompetenceNull: boolean
      ) => {
        if (withEmbedded) {
          if (withCompetenceNull) {
            return supabase
              .from('transactions')
              .select(
                `
          *,
          category:categories(
            id,
            name,
            type,
            color
          )
        `
              )
              .eq('user_id', user.id)
              .is('competence_date', null)
              .gte('date', period.start_date.toISOString().split('T')[0])
              .lte('date', period.end_date.toISOString().split('T')[0])
              .order('date', { ascending: false });
          }
          return supabase
            .from('transactions')
            .select(
              `
          *,
          category:categories(
            id,
            name,
            type,
            color
          )
        `
            )
            .eq('user_id', user.id)
            .gte('competence_date', period.start_date.toISOString().split('T')[0])
            .lte('competence_date', period.end_date.toISOString().split('T')[0])
            .order('competence_date', { ascending: false });
        }
        // plain fallback sem embedded
        if (withCompetenceNull) {
          return supabase
            .from('transactions')
            .select('*')
            .eq('user_id', user.id)
            .is('competence_date', null)
            .gte('date', period.start_date.toISOString().split('T')[0])
            .lte('date', period.end_date.toISOString().split('T')[0])
            .order('date', { ascending: false });
        }
        return supabase
          .from('transactions')
          .select('*')
          .eq('user_id', user.id)
          .gte('competence_date', period.start_date.toISOString().split('T')[0])
          .lte('competence_date', period.end_date.toISOString().split('T')[0])
          .order('competence_date', { ascending: false });
      };

      let rawData: any[] = [];
      let rawFallback: any[] = [];
      {
        const r1: any = await fetchAccrualChunk(true, false);
        if (r1.error && isPGRST200Error(r1.error)) {
          console.warn('[useAccrualData] PGRST200 category embedded — fallback plain', r1.error.message);
          const r1Plain: any = await fetchAccrualChunk(false, false);
          if (r1Plain.error) throw new Error('Falha ao carregar dados de competência');
          const normalized = normalizePaidFields((r1Plain.data ?? []) as any[]);
          const catIds = normalized.map((x: any) => x.category_id).filter((v: any) => v != null);
          const catsMap = await fetchCategoriesMap(catIds, user.id);
          rawData = normalized.map((row: any) => ({
            ...row,
            category: row.category_id ? (catsMap.get(Number(row.category_id)) ?? catsMap.get(row.category_id) ?? null) : null,
            categories: row.category_id ? (catsMap.get(Number(row.category_id)) ?? catsMap.get(row.category_id) ?? null) : null,
          }));
        } else if (r1.error) {
          console.error('Supabase query error:', r1.error);
          throw new Error('Falha ao carregar dados de competência');
        } else {
          rawData = normalizePaidFields((r1.data ?? []) as any[]);
        }

        const r2: any = await fetchAccrualChunk(true, true);
        if (r2.error && isPGRST200Error(r2.error)) {
          console.warn('[useAccrualData] PGRST200 category embedded (fallback) — usando plain', r2.error.message);
          const r2Plain: any = await fetchAccrualChunk(false, true);
          if (r2Plain.error) {
            console.error('Fallback query error:', r2Plain.error);
          } else {
            const normalized2 = normalizePaidFields((r2Plain.data ?? []) as any[]);
            const catIds2 = normalized2.map((x: any) => x.category_id).filter((v: any) => v != null);
            const catsMap2 = await fetchCategoriesMap(catIds2, user.id);
            rawFallback = normalized2.map((row: any) => ({
              ...row,
              category: row.category_id ? (catsMap2.get(Number(row.category_id)) ?? catsMap2.get(row.category_id) ?? null) : null,
              categories: row.category_id ? (catsMap2.get(Number(row.category_id)) ?? catsMap2.get(row.category_id) ?? null) : null,
            }));
          }
        } else if (r2.error) {
          console.error('Fallback query error:', r2.error);
        } else {
          rawFallback = normalizePaidFields((r2.data ?? []) as any[]);
        }
      }

      // Combine results
      const allData = [...(rawData || []), ...(rawFallback || [])];

      console.log(
        `Found ${allData.length} transactions for ${period.label}`
      );

      // Transform to AccrualTransaction format
      let transactions: AccrualTransaction[] = allData.map(row =>
        transformToAccrualTransaction(row, row.category?.name)
      );

      // Apply filters if provided
      if (filters) {
        if (filters.status && filters.status.length > 0) {
          transactions = transactions.filter(t =>
            filters.status!.includes(t.status)
          );
        }

        if (filters.type && filters.type.length > 0) {
          transactions = transactions.filter(t =>
            filters.type!.includes(t.type)
          );
        }

        if (filters.category && filters.category.length > 0) {
          transactions = transactions.filter(t =>
            filters.category!.includes(t.category)
          );
        }

        if (filters.client_search) {
          const search = filters.client_search.toLowerCase();
          transactions = transactions.filter(
            t =>
              t.client_name?.toLowerCase().includes(search) ||
              t.supplier_name?.toLowerCase().includes(search) ||
              t.description.toLowerCase().includes(search)
          );
        }

        if (filters.amount_min !== undefined) {
          transactions = transactions.filter(
            t => t.amount >= filters.amount_min!
          );
        }

        if (filters.amount_max !== undefined) {
          transactions = transactions.filter(
            t => t.amount <= filters.amount_max!
          );
        }

        if (filters.show_recurring_only) {
          transactions = transactions.filter(t => t.is_recurring);
        }

        if (filters.show_overdue_only) {
          transactions = transactions.filter(
            t => t.status === 'em_atraso' as any
          );
        }

        // Apply sorting
        if (filters.sort_by) {
          const direction = filters.sort_direction === 'desc' ? -1 : 1;

          transactions.sort((a, b) => {
            let aVal: any;
            let bVal: any;

            switch (filters.sort_by) {
              case 'competence_date':
                aVal = a.competence_date.getTime();
                bVal = b.competence_date.getTime();
                break;
              case 'due_date':
                aVal = a.due_date.getTime();
                bVal = b.due_date.getTime();
                break;
              case 'amount':
                aVal = a.amount;
                bVal = b.amount;
                break;
              case 'status':
                aVal = a.status;
                bVal = b.status;
                break;
              case 'category':
                aVal = a.category;
                bVal = b.category;
                break;
              default:
                return 0;
            }

            if (aVal < bVal) return -direction;
            if (aVal > bVal) return direction;
            return 0;
          });
        }
      }

      // Calculate summary and groupings
      const summary = calculateAccrualSummary(transactions, period);
      const by_category = groupByCategory(transactions);
      const by_status = groupByStatus(transactions);

      const grouped: AccrualGrouped = {
        period,
        transactions,
        summary,
        by_category,
        by_status,
      };

      return grouped;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    refetchOnWindowFocus: true,
    retry: 2,
  });

  // Subscribe to realtime changes on transactions table
  useEffect(() => {
    if (!enabled) return;

    console.log('🔴 Setting up Supabase Realtime subscription for accrual data');

    const channel = supabase
      .channel(`accrual-transactions-changes_${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'transactions',
        },
        (payload) => {
          console.log('🔄 Transaction change detected:', payload.eventType);
          // Invalidate and refetch accrual data when any transaction changes
          queryClient.invalidateQueries({
            queryKey: ['accrual-data'],
            refetchType: 'active'
          });
        }
      )
      .subscribe();

    return () => {
      console.log('🔴 Cleaning up Supabase Realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [enabled, queryClient]);

  return {
    data: queryResult.data || null,
    transactions: queryResult.data?.transactions || [],
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error as Error | null,
    refetch: queryResult.refetch,
  };
}
